import { Component } from 'react';
import { isChunkLoadError, reloadForNewVersion } from '../../utils/chunkReload';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      // A failed chunk load after a deploy: a new version, not a broken page.
      newVersion: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, newVersion: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    // Reload onto the new build, once. If that already happened a moment ago
    // the failure is real, so fall through to the error screen.
    if (isChunkLoadError(error) && reloadForNewVersion()) return;

    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
      newVersion: false,
    });

    // In production, you would send this to an error reporting service
    // e.g., Sentry, LogRocket, etc.
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // Optionally reload the page or reset app state
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError && this.state.newVersion) {
      return (
        <div className="loading-container" role="status">
          <div className="loading-spinner" aria-hidden="true"></div>
          <p>Loading the latest version...</p>
        </div>
      );
    }

    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary" role="alert">
          <div className="error-boundary-content">
            <h2>This page hit an error</h2>
            <p>Reloading the page usually fixes it. If it keeps happening, let the equipment desk know what you were doing.</p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error details (development only)</summary>
                <pre>{this.state.error.toString()}</pre>
                <pre>{this.state.errorInfo?.componentStack}</pre>
              </details>
            )}

            <div className="error-actions">
              <button
                onClick={this.handleReset}
                className="btn btn-primary"
                data-testid="error-reset-btn"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="btn btn-secondary"
                data-testid="error-home-btn"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
